/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable react-refresh/only-export-components */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Angle,
  Element,
  ElementType,
  Fixture as FixtureType,
  getThickness,
  Group,
  LineType,
  Polyline,
  Rect,
  Symbol as SymbolType,
  Text,
  Trapezoid,
  Triangle,
} from '@ritabygglov/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DeleteCommand } from '../../commands/DeleteCommand';
import { UpdateCommand } from '../../commands/UpdateCommand';
import {
  COLOR_HANDLE,
  CONNECTING_POINT_RADIUS,
  DIMENSION_TICK_STROKE_WIDTH,
} from '../../constants';
import { useAppState } from '../../context/AppState/useAppState';
import { useElementState } from '../../context/AppState/useElementState';
import {
  computeGroupBBox,
  enterGroupEditingIfGrouped,
} from '../../helpers/groupHelpers';
import {
  calculateCrossingLine,
  copy,
  getCappedHandleScale,
  getCrossingPoints,
  getInnerWallStrokeWidth,
  setRotatedRectHeight,
} from '../../helpers/utility';
import { toggleDimensionSide } from '../../services/Actions/Polyline';
import CanvasSession from '../../services/CanvasSession';
import {
  Corner,
  Edge,
  SelectionMode,
  SelectionModes,
  Side,
  ViewBox,
} from '../../types';
import { fixtureRegistry } from '../../services/fixtureRegistry';
import { symbolRegistry } from '../../services/symbolRegistry';
import { PolylineDimensions } from './Dimensions/PolylineDimensions';
import { RectDimensions } from './Dimensions/RectDimensions';
import { AngleDimensions } from './Dimensions/AngleDimensions';
import { TrapezoidDimensions } from './Dimensions/TrapezoidDimensions';
import { CornerAngles } from './Dimensions/CornerAngles';
import { TriangleDimensions } from './Dimensions/TriangleDimensions';
import { getFillPattern } from './Patterns';
import {
  RenderApexHandle,
  RenderCornerSelectors,
  RenderGhostLine,
  RenderLineOutline,
  RenderLineResizeHandles,
  RenderRadiusHandles,
  RenderRectOutline,
  RenderSplitHandles,
  RenderPerpendicularGuide,
  RenderShapeOutline,
} from './ResizeHandlers';
import { RenderEdgeSelectors } from './EdgeSelectors';
import { PatternFill } from './PatternFill';
import {
  createLine,
  createRoundedLinePerPoint,
  getArrowStyle,
  getHitAreaStrokeWidth,
  hasVisibleFill,
  renderHitArea,
  renderOutlinedInnerWall,
  renderSvgElement,
} from './shapes';

export const getCornerCoords = (e: Element) => {
  if (
    e.type === ElementType.Rect ||
    e.type === ElementType.Text ||
    e.type === ElementType.Symbol ||
    e.type === ElementType.Fixture ||
    e.type === ElementType.Triangle ||
    e.type === ElementType.Trapezoid ||
    e.type === ElementType.Angle
  ) {
    return {
      tL: { x: e.x, y: e.y },
      tR: { x: e.x + e.width, y: e.y },
      bR: { x: e.x + e.width, y: e.y + e.height },
      bL: { x: e.x, y: e.y + e.height },
      left: { x: e.x, y: e.y + e.height / 2 },
      right: { x: e.x + e.width, y: e.y + e.height / 2 },
      top: { x: e.x + e.width / 2, y: e.y },
      bottom: { x: e.x + e.width, y: e.y + e.height },
    };
  } else {
    return {
      tL: { x: 0, y: 0 },
      tR: { x: 0, y: 0 },
      bR: { x: 0, y: 0 },
      bL: { x: 0, y: 0 },
    };
  }
};

const fontFamily = `
@font-face {
  font-family: 'Kalam';
  font-style: normal;
  font-weight: 400;
  src: local('Kalam'), url('https://fonts.cdnfonts.com/s/13130/Kalam-Regular.woff') format('woff');
}
`;

// @ts-ignore
export const EditableInput = ({
  x,
  y,
  width,
  height,
  id,
  isEnabled,
  text,
  onChange,
  onHeightChange,
  onBlur,
  style,
  classes,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  id: string;
  isEnabled: boolean;
  text: string;
  onChange: React.FormEventHandler<HTMLTextAreaElement>;
  onHeightChange: (newHeight: number) => void;
  onBlur: () => void;
  style?: React.CSSProperties;
  classes: string;
}) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEnabled && ref.current) {
      ref.current.focus();
      ref.current.select();
      const id = requestAnimationFrame(() => {
        ref.current?.select();
      });
      return () => cancelAnimationFrame(id);
    }
  }, [isEnabled]);

  const updateHeight = useCallback(() => {
    if (ref.current) {
      ref.current.style.height = '1px';
      const scrollHeight = ref.current.scrollHeight;
      ref.current.style.height = scrollHeight + 'px';
      if (scrollHeight > height) {
        onHeightChange(scrollHeight);
      }
    }
  }, [ref, height, onHeightChange]);

  const onKeyUp = () => {
    updateHeight();
  };

  useEffect(() => {
    updateHeight();
  }, [text, updateHeight]);

  const textareaStyle: React.CSSProperties = {
    ...style,
    pointerEvents: isEnabled ? 'auto' : 'none',
  };

  const stopPropagationIfEnabled = (e: React.SyntheticEvent) => {
    if (isEnabled) {
      e.stopPropagation();
    }
  };

  return (
    <foreignObject
      fontSize={14}
      x={x}
      y={y}
      width={width}
      height={height}
      id={id}
      className={classes}
      style={{ pointerEvents: isEnabled ? 'auto' : 'none' }}
    >
      <div
        className="textContainer"
        // eslint-disable-next-line react/no-unknown-property
        data-xmlns="http://www.w3.org/1999/xhtml"
        id={id}
      >
        <style dangerouslySetInnerHTML={{ __html: fontFamily }} />
        <textarea
          className="textInput"
          data-testid="editableInput"
          ref={ref}
          id={id}
          onInput={onChange}
          onKeyUp={onKeyUp}
          onBlur={onBlur}
          onMouseDown={stopPropagationIfEnabled}
          onMouseUp={stopPropagationIfEnabled}
          onClick={stopPropagationIfEnabled}
          onDoubleClick={stopPropagationIfEnabled}
          suppressContentEditableWarning
          value={text}
          style={textareaStyle}
        />
      </div>
    </foreignObject>
  );
};

const SquareRenderer = ({
  isSelected,
  isSoleSelection,
  isHovering,
  viewBox,
  selectionMode,
  classes,
  element,
  session,
  scalingFactor,
}: {
  isSelected: boolean;
  isSoleSelection: boolean;
  isHovering: boolean;
  viewBox: ViewBox;
  selectionMode: SelectionMode;
  classes: string;
  element: Rect | Text | Triangle | Trapezoid | Angle;
  session: CanvasSession | null;
  scalingFactor: number;
}) => {
  const {
    commandManager,
    draggingDimensionId,
    setSelectionMode,
    setSelectedElements,
    selectedElements,
    showTriangleAngles,
    elementsRef,
    selectedAnchorRef,
  } = useElementState();

  // @ts-ignore
  const onChangeInput: React.FormEventHandler<HTMLTextAreaElement> = (e) => {
    const elementCopy = copy(element);
    if (elementCopy && session) {
      const textarea = e.currentTarget;
      textarea.style.height = '1px';
      const newHeight = Math.max(element.height, textarea.scrollHeight);
      textarea.style.height = newHeight + 'px';

      const command = new UpdateCommand(
        [
          {
            ...elementCopy,
            text: textarea.value,
            ...setRotatedRectHeight(element, element.rotate, newHeight),
          },
        ],
        [element],
        session?.currentUserId,
        false
      );
      commandManager.executeCommand(command);
    }
  };

  const onHeightChange = useCallback(
    (newHeight: number) => {
      const elementCopy = copy(element);
      if (elementCopy && session) {
        const anchor = selectedAnchorRef.current;
        const keepBottom =
          selectionMode.type === SelectionModes.Resizing &&
          (anchor === Corner.TopLeft ||
            anchor === Corner.TopRight ||
            anchor === Edge.Top);
        const command = new UpdateCommand(
          [
            {
              ...elementCopy,
              ...setRotatedRectHeight(
                element,
                element.rotate,
                newHeight,
                keepBottom
              ),
            },
          ],
          [element],
          session.currentUserId,
          true
        );
        commandManager.executeCommand(command);
      }
    },
    [element, session, commandManager, selectedAnchorRef, selectionMode.type]
  );

  // @ts-ignore
  const isEnabled =
    isSelected && selectionMode.type === SelectionModes.TextEditing;

  const onEnterTextEdit = () => {
    if (element.type !== ElementType.Text) return;
    if (
      selectionMode.type === SelectionModes.TextEditing &&
      selectedElements[0] === element.id
    ) {
      return;
    }
    setSelectedElements([element.id]);
    setSelectionMode({
      ...selectionMode,
      type: SelectionModes.TextEditing,
      elementType: ElementType.Text,
    });
  };

  const onTextBlur = () => {
    if (element.type !== ElementType.Text) return;
    if (element.text === '' && session) {
      const command = new DeleteCommand([element], session.currentUserId);
      commandManager.executeCommand(command);
    }
  };

  const onDoubleClick = () => {
    const enteredGroup = enterGroupEditingIfGrouped(
      element.id,
      elementsRef.current,
      selectionMode,
      setSelectionMode,
      setSelectedElements
    );
    if (enteredGroup) return;
    if (element.type === ElementType.Text) onEnterTextEdit();
  };

  const {
    type,
    renderingOrder,
    userVersion,
    showDimensions,
    diagonalHatch,
    pattern,
    patternSize,
    ...props
  } = element;
  const { x, y, width, height, rotate } = props;
  const patternValue = getFillPattern(pattern, diagonalHatch, patternSize);
  const style = { ...props.style };

  const isBodyHittable =
    element.type === ElementType.Text || hasVisibleFill(style, patternValue);

  let visibleStyle: React.CSSProperties = style;

  if (!isBodyHittable) {
    visibleStyle = {
      ...style,
      pointerEvents: isSelected ? 'all' : 'none',
    };
  }

  const midX = x + width / 2;
  const midY = y + height / 2;
  return (
    <g
      key={`g-${element.id}`}
      transform={`rotate(${rotate}, ${midX}, ${midY})`}
      onDoubleClick={onDoubleClick}
    >
      <g>
        {patternValue && (
          <PatternFill
            element={element}
            x={x}
            y={y}
            width={width}
            height={height}
            patternValue={patternValue}
          />
        )}
        {renderSvgElement(element, classes, visibleStyle)}
        {renderOutlinedInnerWall(element, classes)}
        {element.type !== ElementType.Text &&
          renderHitArea(element, viewBox.scale)}
        {element.showDimensions && element.type === ElementType.Rect && (
          <RectDimensions
            element={element}
            scalingFactor={scalingFactor}
            scale={viewBox.scale}
            draggingDimensionId={draggingDimensionId}
          />
        )}
        {element.showDimensions && element.type === ElementType.Triangle && (
          <TriangleDimensions
            element={element as Triangle}
            scalingFactor={scalingFactor}
            scale={viewBox.scale}
            draggingDimensionId={draggingDimensionId}
          />
        )}
        {element.showDimensions && element.type === ElementType.Trapezoid && (
          <TrapezoidDimensions
            element={element as Trapezoid}
            scalingFactor={scalingFactor}
            scale={viewBox.scale}
            draggingDimensionId={draggingDimensionId}
          />
        )}
        {element.showDimensions && element.type === ElementType.Angle && (
          <AngleDimensions
            element={element as Angle}
            scalingFactor={scalingFactor}
            scale={viewBox.scale}
            draggingDimensionId={draggingDimensionId}
          />
        )}
        {isSelected &&
          showTriangleAngles &&
          (element.type === ElementType.Triangle ||
            element.type === ElementType.Angle) && (
            <CornerAngles
              element={element as Triangle | Angle}
              scale={viewBox.scale}
            />
          )}
      </g>
      {isSelected && (
        <RenderRectOutline
          id={element.id}
          x={x}
          y={y}
          width={width}
          height={height}
          scale={viewBox.scale}
        />
      )}
      {isHovering && (
        <RenderShapeOutline element={element} scale={viewBox.scale} />
      )}
      {element.type === ElementType.Text && (
        <EditableInput
          x={x}
          y={y}
          width={width}
          height={height}
          id={element.id}
          isEnabled={isEnabled}
          text={element.text}
          onChange={onChangeInput}
          onHeightChange={onHeightChange}
          onBlur={onTextBlur}
          style={style}
          classes={classes}
        />
      )}
      {isSelected &&
        !isEnabled &&
        isSoleSelection &&
        RenderEdgeSelectors(element, viewBox.scale)}
      {isSelected &&
        !isEnabled &&
        isSoleSelection &&
        RenderCornerSelectors(
          element,
          viewBox.scale,
          true,
          true,
          element.type === ElementType.Text
        )}
      {isSelected &&
        !isEnabled &&
        (element.type === ElementType.Triangle ||
          element.type === ElementType.Angle) &&
        RenderApexHandle(element, viewBox.scale)}
    </g>
  );
};

const PolylineRenderer = ({
  element,
  classes,
  isSelected,
  isSoleSelection,
  isHovering,
  session,
  scalingFactor,
  viewBox,
}: {
  classes: string;
  element: Polyline;
  isSelected: boolean;
  isSoleSelection: boolean;
  isHovering: boolean;
  session: CanvasSession;
  scalingFactor: number;
  viewBox: ViewBox;
}) => {
  const { scale } = viewBox;
  const {
    type,
    renderingOrder,
    points,
    id,
    userVersion,
    showDimensions,
    lineType,
    rightArrow,
    leftArrow,
    isOutlined,
    diagonalHatch,
    pattern,
    thickness,
    thicknessAnchor,
    ...props
  } = element;
  const arrowStyle = props.style?.stroke;
  const connectedPointOffset = 2 * getCappedHandleScale(scale);
  const patternValue = getFillPattern(pattern, diagonalHatch);

  const {
    commandManager,
    draggingDimensionId,
    selectionMode,
    setSelectionMode,
    setSelectedElements,
    perpendicularGuideEnabled,
    elementsRef,
  } = useElementState();

  const onDoubleClick = () => {
    enterGroupEditingIfGrouped(
      element.id,
      elementsRef.current,
      selectionMode,
      setSelectionMode,
      setSelectedElements
    );
  };

  const [hoveredSegment, setHoveredSegment] = useState<number | null>(null);
  const [hoveredRadiusCorner, setHoveredRadiusCorner] = useState<number | null>(
    null
  );

  const canShowRadiusHandles =
    selectionMode.type === SelectionModes.None ||
    selectionMode.type === SelectionModes.Selected ||
    selectionMode.type === SelectionModes.RadiusDragging;

  useEffect(() => {
    if (!canShowRadiusHandles) {
      setHoveredRadiusCorner(null);
    }
  }, [canShowRadiusHandles]);

  const shouldShowPerpendicularGuide =
    perpendicularGuideEnabled &&
    element.lineType === LineType.Dimension &&
    element.points.length >= 2 &&
    isSelected;

  const style = { ...props.style };
  // The visible line is non-interactive so the wide invisible hit-area below
  // handles selection (and a closed line's interior stays click-through). Once
  // selected the line itself becomes grabbable for moving.
  const visiblePolylineStyle: React.CSSProperties = {
    ...style,
    strokeWidth: getThickness(element),
    pointerEvents: isSelected ? 'all' : 'none',
  };

  const onToggleDimensionSide = (pointIndex: number) => {
    const elementCopy = copy(element);

    const updatedElement = {
      ...elementCopy,
      points: elementCopy.points.map(toggleDimensionSide(pointIndex)),
    };
    const isEphemeral = false;
    const command = new UpdateCommand(
      [updatedElement],
      [element],
      session.currentUserId,
      isEphemeral
    );
    commandManager.executeCommand(command);
  };

  const processedPoints: [number, number, boolean][] = points.map((p) => {
    if (p.connectingPointIndex !== undefined) {
      const point = points[p.connectingPointIndex];
      if (point) {
        return [point.x, point.y, true];
      }
    }
    return [p.x, p.y, false];
  });

  const processedPointsWithRadius: [number, number, boolean, number][] =
    points.map((p) => {
      if (p.connectingPointIndex !== undefined) {
        const point = points[p.connectingPointIndex];
        if (point) {
          return [point.x, point.y, true, 0];
        }
      }
      return [p.x, p.y, false, p.cornerRadius ?? 0];
    });

  const linePath = createRoundedLinePerPoint(processedPointsWithRadius);

  const hasCornerRadius = points.some((p) => (p.cornerRadius ?? 0) > 0);
  const ghostLinePath = createLine(processedPoints);

  return (
    <g id={`g-${id}`} onDoubleClick={onDoubleClick}>
      {element.type === ElementType.Polyline &&
        element.leftArrow &&
        element.rightArrow && (
          <defs key={`marker-key-${id}`}>
            <marker
              id={`${id}-arrow`}
              key={`${id}-arrow`}
              orient="auto"
              markerWidth="4"
              markerHeight="6"
              refX="2.5"
              refY="3"
            >
              {getArrowStyle(Side.left, element.leftArrow, arrowStyle)};
            </marker>
            <marker
              id={`${id}-arrow-reverse`}
              key={`${id}-arrow-reverse`}
              orient="auto"
              markerWidth="4"
              markerHeight="6"
              refX="1.5"
              refY="3"
            >
              {getArrowStyle(Side.right, element.rightArrow, arrowStyle)};
            </marker>
          </defs>
        )}

      {patternValue && (
        <polygon
          id={id}
          points={processedPoints.map(([x, y]) => `${x},${y}`).join(' ')}
          fill={patternValue}
        />
      )}

      {isSelected &&
        processedPoints
          .filter((p) => p[2])
          .map(([x, y], i) => (
            <circle
              r={CONNECTING_POINT_RADIUS * viewBox.scale}
              cx={x - connectedPointOffset}
              cy={y - connectedPointOffset}
              fill={'#e9f5f8'}
              stroke={COLOR_HANDLE}
              key={'connected-point' + id + i}
            />
          ))}

      {isSelected && isSoleSelection && hasCornerRadius && (
        <RenderGhostLine id={id} pathData={ghostLinePath} scale={scale} />
      )}

      <path
        key={id}
        id={id}
        {...props}
        style={visiblePolylineStyle}
        d={linePath}
        fill="transparent"
        className={classes}
        markerStart={`url(#${id}-arrow-reverse)`}
        markerEnd={`url(#${id}-arrow)`}
        data-testid="polyline"
        stroke={element.stroke || 'black'}
      ></path>
      <path
        key={`hit-${id}`}
        id={id}
        d={linePath}
        fill="none"
        stroke="transparent"
        strokeWidth={getHitAreaStrokeWidth(getThickness(element), scale)}
        className="hit-area"
        data-testid="polyline-hit-area"
      ></path>
      {element.isOutlined && (
        <path
          key={'inner-wall' + id}
          id={id}
          style={{ pointerEvents: 'none' }}
          d={linePath}
          strokeWidth={getInnerWallStrokeWidth(getThickness(element))}
          stroke={'white'}
          fill="transparent"
          className={classes}
          markerStart={`url(#${id}-arrow-reverse)`}
          markerEnd={`url(#${id}-arrow)`}
          data-testid="polyline"
        ></path>
      )}

      {element.lineType === LineType.Dimension &&
        getCrossingPoints(processedPoints).map(([x, y]) => (
          <path
            key={'cross-line-' + x + id}
            id={id}
            style={{ pointerEvents: 'none' }}
            d={createLine(calculateCrossingLine(x, y, 10))}
            strokeWidth={DIMENSION_TICK_STROKE_WIDTH}
            stroke={'black'}
            fill="transparent"
            className={classes}
            markerStart={`url(#${id}-arrow-reverse)`}
            markerEnd={`url(#${id}-arrow)`}
            data-testid="polyline"
          ></path>
        ))}

      {element.showDimensions && (
        <PolylineDimensions
          element={element}
          scalingFactor={scalingFactor}
          scale={scale}
          onToggleDimensionSide={onToggleDimensionSide}
          lineType={element.lineType}
          draggingDimensionId={draggingDimensionId}
        />
      )}
      {(isHovering || isSelected) && (
        <RenderLineOutline id={element.id} pathData={linePath} scale={scale} />
      )}
      {isSelected && isSoleSelection && lineType === LineType.Line && (
        <RenderSplitHandles
          processedPoints={processedPoints}
          scale={scale}
          id={element.id}
          hoveredSegment={hoveredSegment}
          setHoveredSegment={setHoveredSegment}
        />
      )}
      {isSelected && isSoleSelection && canShowRadiusHandles && (
        <RenderRadiusHandles
          points={points}
          processedPoints={processedPoints}
          id={element.id}
          scale={scale}
          scalingFactor={scalingFactor}
          hoveredCorner={hoveredRadiusCorner}
          setHoveredCorner={setHoveredRadiusCorner}
          isRadiusDragging={
            selectionMode.type === SelectionModes.RadiusDragging
          }
        />
      )}
      {shouldShowPerpendicularGuide && (
        <RenderPerpendicularGuide
          id={element.id}
          processedPoints={processedPoints}
          scale={scale}
        />
      )}
      {isSelected && isSoleSelection && (
        <RenderLineResizeHandles
          processedPoints={processedPoints}
          scale={scale}
          id={element.id}
        />
      )}
    </g>
  );
};

const SymbolRenderer = ({
  classes,
  element,
  isSelected,
  isSoleSelection,
  isHovering,
  viewBox,
}: {
  isSelected: boolean;
  isSoleSelection: boolean;
  isHovering?: boolean;
  viewBox: ViewBox;
  classes: string;
  element: SymbolType;
  session: CanvasSession | null;
  scalingFactor: number;
}) => {
  const { appState, selectionMode, setSelectionMode, setSelectedElements } =
    useAppState();
  const onDoubleClick = () => {
    enterGroupEditingIfGrouped(
      element.id,
      appState.elements,
      selectionMode,
      setSelectionMode,
      setSelectedElements
    );
  };
  const { x, y, width, height, rotate, symbolType, ...props } = element;
  const style = { ...props.style };
  const SymbolIcon = symbolRegistry[symbolType].canvasIcon;
  const icon = (
    <SymbolIcon
      x={x}
      y={y}
      id={element.id}
      style={style}
      // currently each symbol is 16x16, so we scale it to fit the element size
      scale={Math.min(element.width, element.height) / 16}
    />
  );

  const renderElement = (
    <g id={element.id} className={classes}>
      {icon}
    </g>
  );
  const midX = x + width / 2;
  const midY = y + height / 2;

  return (
    <g
      key={`g-${element.id}`}
      transform={`rotate(${rotate}, ${midX}, ${midY})`}
      onDoubleClick={onDoubleClick}
    >
      {renderElement}
      {(isHovering || isSelected) && (
        <RenderRectOutline
          id={element.id}
          x={x}
          y={y}
          width={width}
          height={height}
          scale={viewBox.scale}
        />
      )}
      {isSelected &&
        isSoleSelection &&
        RenderCornerSelectors(element, viewBox.scale, true, true)}
    </g>
  );
};

const FixtureRenderer = ({
  classes,
  element,
  isSelected,
  isSoleSelection,
  isHovering,
  viewBox,
}: {
  isSelected: boolean;
  isSoleSelection: boolean;
  isHovering?: boolean;
  viewBox: ViewBox;
  classes: string;
  element: FixtureType;
  session: CanvasSession | null;
  scalingFactor: number;
}) => {
  const { appState, selectionMode, setSelectionMode, setSelectedElements } =
    useAppState();
  const onDoubleClick = () => {
    enterGroupEditingIfGrouped(
      element.id,
      appState.elements,
      selectionMode,
      setSelectionMode,
      setSelectedElements
    );
  };
  const { x, y, width, height, rotate, fixtureType, mirroredX, mirroredY } =
    element;
  const FixtureIcon = fixtureRegistry[fixtureType].icon;
  const icon = (
    <FixtureIcon
      x={x}
      y={y}
      id={element.id}
      width={width}
      height={height}
      mirroredX={mirroredX}
      mirroredY={mirroredY}
    />
  );
  const renderElement = (
    <g id={element.id} className={classes}>
      {icon}
    </g>
  );
  const midX = x + width / 2;
  const midY = y + height / 2;
  return (
    <g
      key={`g-${element.id}`}
      transform={`rotate(${rotate}, ${midX}, ${midY})`}
      onDoubleClick={onDoubleClick}
    >
      {renderElement}
      {(isHovering || isSelected) && (
        <RenderRectOutline
          id={element.id}
          x={x}
          y={y}
          width={width}
          height={height}
          scale={viewBox.scale}
        />
      )}
      {isSelected &&
        isSoleSelection &&
        RenderEdgeSelectors(element, viewBox.scale)}
      {isSelected &&
        isSoleSelection &&
        RenderCornerSelectors(element, viewBox.scale, true, true)}
    </g>
  );
};

const GroupRenderer = ({
  element,
  elements,
  isSelected,
  isHovering,
  isEditing,
}: {
  element: Group;
  elements: { [id: string]: Element };
  isSelected: boolean;
  isHovering: boolean;
  isEditing: boolean;
}) => {
  if (!isSelected && !isHovering && !isEditing) return null;

  // While editing, live-recompute (but don't persist) the local-frame bbox
  // so it hugs children as they move; otherwise the group's own stored
  // fields are already authoritative. Both are expressed in the same local
  // frame, pivoted at the group's own (possibly rotated) center.
  const bbox = isEditing
    ? computeGroupBBox(element, elements)
    : {
        x: element.x,
        y: element.y,
        width: element.width,
        height: element.height,
      };
  if (!bbox) return null;

  const cx = element.x + element.width / 2;
  const cy = element.y + element.height / 2;

  return (
    <g
      transform={
        element.rotate ? `rotate(${element.rotate}, ${cx}, ${cy})` : undefined
      }
    >
      <rect
        id={element.id}
        x={bbox.x}
        y={bbox.y}
        width={bbox.width}
        height={bbox.height}
        fill="none"
        stroke={COLOR_HANDLE}
        strokeWidth={1}
        strokeDasharray="6 4"
        style={{ pointerEvents: 'none' }}
      />
    </g>
  );
};

const defaultExports = {
  Square: React.memo(SquareRenderer),
  Polyline: React.memo(PolylineRenderer),
  Symbol: React.memo(SymbolRenderer),
  Fixture: React.memo(FixtureRenderer),
  Group: React.memo(GroupRenderer),
};

export default defaultExports;
